/**
 * Brand-token resolution for the CanvasKit editor's RENDER side.
 *
 * The editor store keeps the RAW document as its source of truth: a fill/stroke
 * SOLID paint may hold a brand token like `$brand.color.primary` instead of a
 * hex literal (see the ColorPicker brand-swatch row). Persistence, undo, the
 * `data-nm-doc` embed, and `onDocumentChange` all carry that RAW doc so a brand
 * swap re-themes the email and the server compile re-resolves independently
 * (resolveMergeTagsInTree runs on a clone at compile time).
 *
 * But Skia's `parseColor` can't read a token — it renders black. So the render
 * pipeline consumes a RESOLVED display clone produced here: every SOLID paint
 * color that carries a token is resolved against the active brand context. This
 * is the single place editor-side color resolution lives; render surfaces call
 * `resolveDocColorsMemo` (memoized per doc identity + brand context) rather than
 * scattering resolution across draw call sites.
 *
 * Colors don't affect layout or measurement, so resolving on the render side is
 * safe: computeLayout / text measurement never read paint colors.
 */
import type { SceneDocument, SceneNode, Paint } from '@/lib/scene-document';

import { resolveMergeTags, type BrandResolverContext } from '@nexmail/shared-util/email-builder';

// Deterministic debug color for a token the active brand context can't resolve
// (a missing brand field). The resolver's own per-usage fallbacks (button,
// text, background, ...) mean this only surfaces for genuinely unknown usages.
// Single source of truth lives in the shared brand-swatch module so the Color
// field chip and the canvas render agree.
export { UNRESOLVED_TOKEN_FALLBACK } from '@/components/shared/brand-swatch';
import { UNRESOLVED_TOKEN_FALLBACK } from '@/components/shared/brand-swatch';

/** True when a color string carries a merge token (only tokens contain `$`). */
export function colorHasToken(color: string | undefined | null): color is string {
  return typeof color === 'string' && color.indexOf('$') !== -1;
}

/**
 * Resolve a single paint color string for DISPLAY.
 *   - No token → returned unchanged.
 *   - Token resolves against `brand` → the resolved CSS color.
 *   - Token present but unresolved (missing brand field) → magenta fallback.
 */
export function resolveColorTokenForDisplay(
  color: string,
  brand: BrandResolverContext | null | undefined
): string {
  if (!colorHasToken(color)) return color;
  const resolved = resolveMergeTags(color, brand ?? undefined, undefined);
  // Still carries a token → the context couldn't resolve it. Fall back rather
  // than hand Skia a token it would paint black.
  return colorHasToken(resolved) ? UNRESOLVED_TOKEN_FALLBACK : resolved;
}

// Resolve the token colors of a SOLID paint array, preserving referential
// identity when nothing changed (so scenes without tokens stay byte-identical
// and the memo below can short-circuit). Only the `color` of SOLID paints is
// touched; gradients, image paints, opacity, order, and visibility are intact.
function resolvePaintArray(
  paints: Paint[] | undefined,
  brand: BrandResolverContext | null | undefined
): Paint[] | undefined {
  if (!paints || paints.length === 0) return paints;
  let next: Paint[] | null = null;
  for (let i = 0; i < paints.length; i++) {
    const p = paints[i];
    if (p.type === 'SOLID' && colorHasToken(p.color)) {
      const resolved = resolveColorTokenForDisplay(p.color, brand);
      if (resolved !== p.color) {
        if (!next) next = paints.slice();
        next[i] = { ...p, color: resolved };
      }
    }
  }
  return next ?? paints;
}

/**
 * Return a display clone of `doc` with every token-valued SOLID fill/stroke
 * color (nodes + document background) resolved against `brand`. Returns the
 * SAME doc object when nothing carried a token, so token-free scenes are
 * untouched and cheap.
 */
export function resolveDocColors(
  doc: SceneDocument,
  brand: BrandResolverContext | null | undefined
): SceneDocument {
  let changedNodes: Record<string, SceneNode> | null = null;
  for (const id of Object.keys(doc.nodes)) {
    const node = doc.nodes[id];
    if (!node) continue;
    const nextFills = resolvePaintArray(node.fills, brand);
    const nextStrokes = resolvePaintArray(node.strokes, brand);
    if (nextFills !== node.fills || nextStrokes !== node.strokes) {
      if (!changedNodes) changedNodes = { ...doc.nodes };
      changedNodes[id] = {
        ...node,
        ...(nextFills !== node.fills ? { fills: nextFills } : {}),
        ...(nextStrokes !== node.strokes ? { strokes: nextStrokes } : {}),
      };
    }
  }

  const nextDocFills = resolvePaintArray(doc.fills, brand);
  if (!changedNodes && nextDocFills === doc.fills) return doc;

  return {
    ...doc,
    ...(changedNodes ? { nodes: changedNodes } : {}),
    ...(nextDocFills !== doc.fills ? { fills: nextDocFills } : {}),
  };
}

// Per-doc-identity memo. Docs are immutable snapshots, so keying on identity is
// safe: an edit produces a new doc object (cache miss → re-resolve); an idle
// frame (hover/zoom/pan/selection) reuses the same object (cache hit). Keyed
// weakly so old drag-frame docs are collected. The stored brand ref is compared
// so a brand swap on the same doc recomputes.
const memo = new WeakMap<
  SceneDocument,
  { brand: BrandResolverContext | null; doc: SceneDocument }
>();

export function resolveDocColorsMemo(
  doc: SceneDocument,
  brand: BrandResolverContext | null | undefined
): SceneDocument {
  const brandKey = brand ?? null;
  const hit = memo.get(doc);
  if (hit && hit.brand === brandKey) return hit.doc;
  const resolved = resolveDocColors(doc, brandKey);
  memo.set(doc, { brand: brandKey, doc: resolved });
  return resolved;
}
